/**
 * Template Card Component
 * Displays a template in the template selection modal with image preview
 */

import React, { useRef } from 'react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import { Image as ImageIcon } from 'lucide-react';

interface TemplateData {
  id: string;
  name: string;
  path: string;
  relativePath: string;
  slideJsonUrl: string;
  previewUrl?: string;
  metadata?: any;
}

interface TemplateCardProps {
  template: TemplateData;
  isSelected: boolean;
  onTemplateClick: (template: TemplateData) => void;
  index: number;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isSelected,
  onTemplateClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle card click
  const handleClick = () => {
    onTemplateClick(template);
  };

  return (
    <Card
      ref={containerRef}
      className={cn(
        'relative overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg group',
        isSelected && 'ring-2 ring-primary'
      )}
      onClick={handleClick}
    >
      <div className="flex flex-col h-[280px]">
        {/* Image Preview */}
        <div className="flex-1 relative overflow-hidden bg-slate-50 dark:bg-slate-900">
          <div className="w-full h-full flex items-center justify-center p-4">
            {template.previewUrl ? (
              <img
                src={template.previewUrl}
                alt={template.name}
                className="max-w-full max-h-full object-contain rounded"
                loading="lazy"
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No preview available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Template Name - Compact */}
        <div className="flex-shrink-0 px-2.5 py-1.5 bg-card border-t">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground truncate flex-1">
              {template.name}
            </p>
            {isSelected && (
              <div className="flex-shrink-0 text-primary">
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
